import { FC, useCallback } from "react";
import { Button, ButtonProps, Stack, Tooltip } from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import { AIIcon } from "@/assets/icons/app-related/AIIcon";
import { ExploreWithAIButtonV2AnomaliesTooltipTitle } from "@/storybook/explore-with-AI-button/ExploreWithAIButtonV2AnomaliesTooltipTitle";
import {
  ResourceExplorerAnomaliesInsightBreakdown,
  ResourceExplorerFilterSchema,
} from "@/services/cloudchipr.api";

interface ExploreWithAIButtonV2Props {
  onClick(mode: ResourceExplorerFilterSchema["mode"]): void;
  size?: ButtonProps["size"];
  anomaliesCount?: number;
  anomalies?: ResourceExplorerAnomaliesInsightBreakdown[];
}

export const ExploreWithAIButtonV2: FC<ExploreWithAIButtonV2Props> = ({
  onClick,
  size,
  anomaliesCount,
  anomalies,
}) => {
  const anomaliesClickHandler = useCallback(() => {
    onClick("anomaly_detector");
  }, [onClick]);

  const explainWithAiClickHandler = useCallback(() => {
    onClick("anomaly_detection_and_summary");
  }, [onClick]);
  return (
    <Stack
      sx={{
        background: "linear-gradient(180deg, #E0E0FF, #F6EAFF)",
        borderColor: "linear-gradient(180deg, #E0E0FF, #F6EAFF)",
      }}
      borderRadius={7}
      padding={anomaliesCount ? 0.2 : 0}
      direction="row"
      pl={anomaliesCount ? 1.5 : 0}
      spacing={1}
    >
      {!!anomaliesCount && (
        <Tooltip
          componentsProps={{
            tooltip: { sx: customTooltipStyles },
          }}
          placement="bottom"
          title={
            <ExploreWithAIButtonV2AnomaliesTooltipTitle
              anomalies={anomalies}
              onClick={onClick}
            />
          }
          arrow
        >
          <Button
            onClick={anomaliesClickHandler}
            startIcon={<InsightsIcon fontSize="small" color="warning" />}
            sx={anomalyBtnStyle}
            variant="text"
            disableRipple
          >
            {anomaliesCount} Anomalies Detected
          </Button>
        </Tooltip>
      )}

      <Button
        onClick={explainWithAiClickHandler}
        sx={{
          px: 1,
          borderRadius: "24px",
          border: 1,
          whiteSpace: "nowrap",
        }}
        size={size ?? "small"}
        startIcon={
          <AIIcon
            fontSize="small"
            sx={{
              p: size === "xsmall" ? 0.25 : undefined,
            }}
          />
        }
      >
        Explain With AI
      </Button>
    </Stack>
  );
};

const anomalyBtnStyle = {
  backgroundColor: "transparent",
  whiteSpace: "nowrap",
  "&:hover": {
    textDecoration: "underline",
    backgroundColor: "transparent",
    boxShadow: "none",
  },

  "&:active": {
    backgroundColor: "transparent",
    boxShadow: "none",
  },
};

const customTooltipStyles = {
  color: "text.primary",
  bgcolor: "background.paper",
  "& .MuiTooltip-arrow": {
    color: "background.paper",
  },
  boxShadow: 7,
  p: 0,
};
